import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, LogOut, LayoutGrid, History, UserCircle2, Calculator, BarChart3, Bot, BadgeDollarSign } from "lucide-react";
import { Button } from "./ui/button";
import { PrepaymentCalculator } from "./PrepaymentCalculator";
import { VolumeTracker } from "./VolumeTracker";
import { AIAssistant } from "./AIAssistant";

const getUserName = () => {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    return u?.name || u?.username || "";
  } catch {
    return "";
  }
};

export const TopBar = ({ crumbs = [] }) => {
  const navigate = useNavigate();
  const [calcOpen, setCalcOpen] = useState(false);
  const [volumeOpen, setVolumeOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  return (
    <header className="sticky top-0 z-20 border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
      <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <nav className="flex items-center gap-1 text-sm" aria-label="Breadcrumb">
          <Link
            to="/"
            data-testid="nav-home-link"
            className="flex items-center gap-1.5 font-semibold text-foreground transition-colors duration-150 hover:text-primary"
          >
            <LayoutGrid className="h-4 w-4 text-primary" />
            <span className="hidden sm:inline">Affiliate Manager</span>
          </Link>
          {crumbs.map((c, i) => (
            <span key={i} className="flex items-center gap-1">
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
              {c.to ? (
                <Link
                  to={c.to}
                  className="text-muted-foreground transition-colors duration-150 hover:text-foreground"
                >
                  {c.label}
                </Link>
              ) : (
                <span className="font-medium text-foreground">{c.label}</span>
              )}
            </span>
          ))}
        </nav>
        <div className="flex items-center gap-1">
          {getUserName() && (
            <span
              data-testid="topbar-username"
              className="mr-1 hidden items-center gap-1.5 text-sm text-muted-foreground md:inline-flex"
            >
              <UserCircle2 className="h-4 w-4 text-primary" />
              {getUserName()}
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCalcOpen(true)}
            data-testid="nav-calculator-button"
            title="Prepayment Calculator"
          >
            <Calculator className="mr-1.5 h-4 w-4" />
            <span className="hidden sm:inline">Calculator</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/price-list")}
            data-testid="nav-price-list-button"
            title="Price List"
          >
            <BadgeDollarSign className="mr-1.5 h-4 w-4" />
            <span className="hidden sm:inline">Price List</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAiOpen(true)}
            data-testid="nav-ai-assistant-button"
            title="AI Assistant"
          >
            <Bot className="mr-1.5 h-4 w-4" />
            <span className="hidden sm:inline">AI Assistant</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setVolumeOpen(true)}
            data-testid="nav-volume-tracker-button"
            title="Volume Tracker"
          >
            <BarChart3 className="mr-1.5 h-4 w-4" />
            <span className="hidden sm:inline">Volume</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate("/history")} data-testid="nav-history-button">
            <History className="mr-1.5 h-4 w-4" />
            <span className="hidden sm:inline">History</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-button">
            <LogOut className="mr-1.5 h-4 w-4" />
            Logout
          </Button>
        </div>
      </div>
      <PrepaymentCalculator open={calcOpen} onOpenChange={setCalcOpen} />
      <VolumeTracker open={volumeOpen} onOpenChange={setVolumeOpen} />
      <AIAssistant open={aiOpen} onOpenChange={setAiOpen} />
    </header>
  );
};
